import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { api } from '../api/client'
import type { ProjectMessage } from '../api/types'
import { canSend, fileRefusal, timeOf, withDays } from '../lib/projectChat'
import { initials } from './UserMenu'

const message = (failed: unknown) => (failed instanceof Error ? failed.message : 'Something went wrong.')

/** A project's chat (the Projects board, Chat tab): every member posts, an
 *  image may ride along, and alarms on the project's stations post here by
 *  themselves. Oldest first, newest at the bottom; the parent polls. */
export function ProjectChat({ projectId, projectName, messages, onChanged }: {
  projectId: number; projectName: string; messages: ProjectMessage[] | undefined
  onChanged: () => void
}) {
  const [body, setBody] = useState('')
  const [attach, setAttach] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const list = useRef<HTMLOListElement>(null)
  const count = messages?.length ?? 0

  // Keep the newest message in view as messages arrive.
  useEffect(() => {
    const el = list.current
    if (el) el.scrollTop = el.scrollHeight
  }, [count])

  const send = async () => {
    setBusy(true); setError(undefined)
    try {
      await api.projects.postMessage(projectId, body, attach ?? undefined)
      setBody(''); setAttach(null)
      onChanged()
    } catch (failed) {
      setError(message(failed))
    } finally {
      setBusy(false)
    }
  }
  const remove = async (m: ProjectMessage) => {
    try {
      await api.projects.removeMessage(projectId, m.id)
      onChanged()
    } catch (failed) {
      setError(message(failed))
    }
  }
  const pick = (file: File | undefined) => {
    if (!file) return
    const refusal = fileRefusal(file)
    setError(refusal ?? undefined)
    setAttach(refusal ? null : file)
  }

  return (
    <div role="tabpanel" aria-label="Chat" className="pc-chat">
      {messages === undefined ? <p className="muted pj-pad">Loading…</p> : (
        <ol className="pc-list" ref={list} aria-label={`Messages in ${projectName}`}>
          {messages.length === 0 && (
            <li className="pc-empty muted">No messages yet. Alarms and warnings on this project's
              stations post here by themselves.</li>
          )}
          {withDays(messages).map(({ message: m, day }) => (
            <li key={m.id}>
              {day && <div className="pc-day"><span aria-hidden="true" />{day}<span aria-hidden="true" /></div>}
              <div className="pc-msg">
                <span className={`pc-avatar${m.kind === 'event' ? ' is-platform' : ''}`} aria-hidden="true">
                  {m.kind === 'event' ? 'HD' : initials(m.author ?? '?')}
                </span>
                <div className="pc-body">
                  <div className="pc-meta">
                    <span className="pc-who">{m.kind === 'event' ? 'Platform' : m.author}</span>
                    <span className="pc-time mono">{timeOf(m.created_at)}</span>
                    {m.can_remove && (
                      <button type="button" className="link pc-remove"
                              aria-label={`Remove the message of ${timeOf(m.created_at)}`}
                              onClick={() => void remove(m)}>Remove</button>
                    )}
                  </div>
                  {m.kind === 'event' ? (
                    <div className={`pc-event pc-event-${m.level}`}>
                      <span className={`alarm-level alarm-level-${m.level === 'cleared' ? 'cleared' : m.level}`}>
                        <span className="al-dot" />{m.level === 'alarm' ? 'Alarm' : m.level === 'warning' ? 'Warning' : 'Cleared'}
                      </span>
                      <span>{m.body}</span>
                      {m.station && <Link to={`/explore/${m.station.code}`}>Open station</Link>}
                    </div>
                  ) : m.body && <p className="pc-text">{m.body}</p>}
                  {m.image && <>
                    <a className="pc-img" href={m.image.urls.preview} target="_blank" rel="noreferrer"
                       aria-label={`Open image ${m.image.caption}`}>
                      <img src={m.image.urls.thumb} alt="" loading="lazy" />
                      <span>{m.image.caption}</span>
                    </a>
                    <a className="pc-img-original" href={m.image.urls.original} download>Download original</a>
                  </>}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
      <div className="pc-compose">
        <label className="field">
          <span>Message everyone in {projectName}</span>
          <textarea rows={3} value={body} maxLength={4000} placeholder="Write a message"
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSend(body, !!attach)) void send()
                    }} />
        </label>
        {attach && (
          <span className="pc-attached">
            {attach.name}
            <button type="button" aria-label="Remove attached image" onClick={() => setAttach(null)}>×</button>
          </span>
        )}
        <div className="pc-actions">
          {import.meta.env.VITE_DEMO_MODE !== 'true' && <label className="button pc-up">
            Attach image
            <input type="file" accept="image/*,.heic,.heif" className="visually-hidden"
                   onChange={(e) => { pick(e.target.files?.[0]); e.target.value = '' }} />
          </label>}
          <span className="muted pc-hint">
            {import.meta.env.VITE_DEMO_MODE === 'true' ? 'Demo notes stay in this browser. Image attachments require the connected file service.'
              : 'Alarms and warnings post here by themselves.'}
          </span>
          <button type="button" className="button primary" disabled={busy || !canSend(body, !!attach)}
                  onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
        {error && <p className="error" role="alert">{error}</p>}
      </div>
    </div>
  )
}
